import { useEffect, useState } from 'react'
import {
  CheckCircle2,
  Hourglass,
  XCircle,
  Mail,
  Shield,
  Plus,
  Trash2,
  RefreshCw,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { config as runtimeConfig } from '@/config.js'
import api from '@/services/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import Stepper from '@/components/common/Stepper'

/** Los cuatro pasos de la activación, para la vista de conjunto de arriba. */
const PASOS_ACTIVACION = [
  { clave: 'email', titulo: 'Email', descripcion: 'Confirma tu correo' },
  { clave: 'kyc', titulo: 'Identidad', descripcion: 'Verificación KYC' },
  { clave: 'pago', titulo: 'Cobro', descripcion: 'Dónde te pagamos' },
  { clave: 'seguridad', titulo: 'Seguridad', descripcion: 'Doble factor' },
]

const KYC_DOC_TYPES = [
  { value: 'id_front', label: 'Identificación oficial (frente)' },
  { value: 'id_back', label: 'Identificación oficial (reverso)' },
  { value: 'address_proof', label: 'Comprobante de domicilio' },
  { value: 'tax_document', label: 'Documento fiscal (RUC/NIT/RFC/CIF)' },
  { value: 'legal_representation', label: 'Acta constitutiva o poder' },
  { value: 'other', label: 'Otro documento' },
]

const KYC_STATUS = {
  not_submitted: { label: 'No enviado', variant: 'secondary' },
  pending_review: { label: 'En revisión', variant: 'warning' },
  approved: { label: 'Aprobado', variant: 'success' },
  rejected: { label: 'Rechazado', variant: 'danger' },
  needs_info: { label: 'Falta info', variant: 'warning' },
}

function Step({ active, completed, icon, title, badge, children }) {
  const Icon = icon
  return (
    <li className="flex gap-4 last:[&_.line]:hidden">
      <div className="flex flex-col items-center shrink-0">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full border-2 shrink-0',
            completed
              ? 'bg-success border-success text-white'
              : active
                ? 'bg-primary-50 dark:bg-primary-900/40 border-primary text-primary'
                : 'bg-card border-line text-neutral-400 dark:text-neutral-500'
          )}
        >
          {completed ? <CheckCircle2 size={20} /> : <Icon size={18} />}
        </div>
        <div className="line w-0.5 flex-1 bg-line mt-2" />
      </div>
      <div className="pb-8 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <h3
            className={cn(
              'font-display text-h3 font-semibold',
              active || completed ? 'text-foreground' : 'text-subtle'
            )}
          >
            {title}
          </h3>
          {badge}
        </div>
        {(active || completed) && children}
      </div>
    </li>
  )
}

export default function Onboarding() {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(null)
  const [activeStep, setActiveStep] = useState(0)

  const [docType, setDocType] = useState('id_front')
  const [docFileName, setDocFileName] = useState('')
  const [docNotes, setDocNotes] = useState('')
  const [pendingDocs, setPendingDocs] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const [twoFaStatus, setTwoFaStatus] = useState(null)
  const [setup2FA, setSetup2FA] = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [backupCodes, setBackupCodes] = useState(null)

  const KYC_SUPPORT_EMAIL = runtimeConfig.kycSupportEmail

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [kycRes, twoFaRes] = await Promise.all([
        api.get('/partners/kyc/status'),
        api
          .get('/partners/auth/2fa/status')
          .catch(() => ({ data: { data: { enabled: false } } })),
      ])
      setStatus(kycRes.data.data)
      setTwoFaStatus(twoFaRes.data.data)
      const s = kycRes.data.data
      if (!s.emailVerified) setActiveStep(0)
      else if (s.kyc.status !== 'approved') setActiveStep(1)
      else if (!twoFaRes.data.data?.enabled) setActiveStep(2)
      else setActiveStep(3)
    } catch {
      toast.error('Error cargando estado de onboarding')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const addDocReference = () => {
    if (!docType || !docFileName) {
      toast.error('Tipo y nombre de archivo son requeridos')
      return
    }
    setPendingDocs([
      ...pendingDocs,
      {
        type: docType,
        url: `email://${KYC_SUPPORT_EMAIL}/${encodeURIComponent(docFileName)}`,
        fileName: docFileName,
        notes: docNotes,
      },
    ])
    setDocFileName('')
    setDocNotes('')
  }

  const removeDoc = (idx) => setPendingDocs(pendingDocs.filter((_, i) => i !== idx))

  const submitKyc = async () => {
    if (pendingDocs.length === 0) {
      toast.error('Agrega al menos una referencia de documento')
      return
    }
    setSubmitting(true)
    try {
      await api.post('/partners/kyc/submit', { documents: pendingDocs })
      toast.success('Solicitud KYC enviada. Revisaremos en 1-3 días.')
      setPendingDocs([])
      fetchAll()
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error al enviar KYC')
    } finally {
      setSubmitting(false)
    }
  }

  const start2FASetup = async () => {
    try {
      const res = await api.post('/partners/auth/2fa/setup')
      setSetup2FA(res.data.data)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error iniciando setup 2FA')
    }
  }

  const verify2FASetup = async () => {
    if (!/^\d{6}$/.test(verifyCode)) {
      toast.error('Código debe ser 6 dígitos')
      return
    }
    try {
      const res = await api.post('/partners/auth/2fa/verify-setup', { code: verifyCode })
      setBackupCodes(res.data.data?.backupCodes || [])
      setSetup2FA(null)
      setVerifyCode('')
      toast.success('2FA activado')
      fetchAll()
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Código inválido')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    )
  }

  const kycSt = status?.kyc?.status || 'not_submitted'
  const kycInfo = KYC_STATUS[kycSt] || KYC_STATUS.not_submitted

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-8 space-y-6">
      <div>
        <h2 className="font-display text-h1 font-semibold text-foreground leading-tight">
          Activación de tu cuenta de Partner
        </h2>
        <p className="text-body-sm text-subtle mt-1">
          Completa estos pasos para empezar a recibir referidos y comisiones.
        </p>
      </div>

      {/*
        Vista de conjunto ANTES del detalle. La lista de abajo enseña un paso abierto y los
        demás plegados: sirve para trabajar, pero no para saber cuánto falta. Un asesor que
        no ve dónde está abandona la activación a medias, que es exactamente lo que impide
        que empiece a vender.

        Se puede volver a un paso ya hecho: repasar lo que uno subió es lo normal.
      */}
      <Stepper
        pasos={PASOS_ACTIVACION}
        actual={PASOS_ACTIVACION[Math.min(activeStep, PASOS_ACTIVACION.length - 1)].clave}
        habilitados={PASOS_ACTIVACION.map((p) => p.clave)}
        onIr={(clave) => setActiveStep(PASOS_ACTIVACION.findIndex((p) => p.clave === clave))}
      />

      <Card>
        <CardContent className="pt-6">
          <ol>
            {/* PASO 1: Email */}
            <Step
              index={0}
              active={activeStep === 0}
              completed={status?.emailVerified}
              icon={Mail}
              title="Verificar email"
            >
              {status?.emailVerified ? (
                <Alert variant="success">
                  <CheckCircle2 size={16} />
                  <AlertDescription>Email verificado correctamente.</AlertDescription>
                </Alert>
              ) : (
                <Alert variant="warning">
                  <AlertDescription>
                    Te enviamos un email con un enlace de verificación. Revisa tu bandeja
                    (incluyendo spam) y haz clic en el enlace.
                  </AlertDescription>
                </Alert>
              )}
            </Step>

            {/* PASO 2: KYC */}
            <Step
              index={1}
              active={activeStep === 1}
              completed={kycSt === 'approved'}
              icon={Hourglass}
              title="Verificación KYC"
              badge={status?.kyc && <Badge variant={kycInfo.variant}>{kycInfo.label}</Badge>}
            >
              {kycSt === 'approved' && (
                <Alert variant="success">
                  <CheckCircle2 size={16} />
                  <AlertDescription>
                    KYC aprobado. Ya puedes recibir comisiones.
                  </AlertDescription>
                </Alert>
              )}

              {kycSt === 'pending_review' && (
                <Alert variant="info">
                  <AlertDescription>
                    Tus documentos están en revisión. Te notificaremos por email (1-3 días
                    hábiles).
                  </AlertDescription>
                </Alert>
              )}

              {kycSt === 'rejected' && (
                <Alert variant="destructive" className="mb-3">
                  <XCircle size={16} />
                  <AlertDescription>
                    KYC rechazado. Razón:{' '}
                    {status.kyc.rejectionReason || 'Sin especificar'}
                  </AlertDescription>
                </Alert>
              )}

              {(kycSt === 'not_submitted' ||
                kycSt === 'rejected' ||
                kycSt === 'needs_info') &&
                status?.emailVerified && (
                  <div className="space-y-3">
                    <Alert variant="info">
                      <AlertDescription>
                        <p className="font-semibold mb-1">Cómo enviar tus documentos:</p>
                        <ol className="list-decimal pl-5 space-y-0.5 text-body-sm">
                          <li>
                            Envía cada documento por email a{' '}
                            <strong>{KYC_SUPPORT_EMAIL}</strong> con el asunto:{' '}
                            <code>KYC Partner — [Tu organización]</code>
                          </li>
                          <li>Aquí registra una entrada por cada documento.</li>
                          <li>
                            Cuando hayas registrado todos, presiona &quot;Enviar para
                            revisión&quot;.
                          </li>
                        </ol>
                      </AlertDescription>
                    </Alert>

                    <div className="grid gap-2 sm:grid-cols-[220px_1fr_auto]">
                      <Select value={docType} onValueChange={setDocType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KYC_DOC_TYPES.map((d) => (
                            <SelectItem key={d.value} value={d.value}>
                              {d.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={docFileName}
                        onChange={(e) => setDocFileName(e.target.value)}
                        placeholder="ej: ine_juan_perez.pdf"
                      />
                      <Button onClick={addDocReference} variant="outline">
                        <Plus size={14} /> Agregar
                      </Button>
                    </div>

                    <Textarea
                      placeholder="Notas (opcional)"
                      value={docNotes}
                      onChange={(e) => setDocNotes(e.target.value)}
                      rows={2}
                    />

                    {pendingDocs.length > 0 && (
                      <ul className="rounded-md border border-line bg-surface-muted divide-y divide-line">
                        {pendingDocs.map((d, idx) => (
                          <li
                            key={idx}
                            className="flex items-center gap-3 px-3 py-2"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-body-sm font-medium text-foreground truncate">
                                {d.fileName}
                              </p>
                              <p className="text-caption text-subtle truncate">
                                {KYC_DOC_TYPES.find((t) => t.value === d.type)
                                  ?.label || d.type}
                                {d.notes ? ` — ${d.notes}` : ''}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => removeDoc(idx)}
                              className="text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <Button
                      onClick={submitKyc}
                      disabled={pendingDocs.length === 0 || submitting}
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Enviando…
                        </>
                      ) : (
                        'Enviar para revisión'
                      )}
                    </Button>
                  </div>
                )}

              {!status?.emailVerified && (
                <Alert variant="warning">
                  <AlertDescription>
                    Verifica tu email primero antes de iniciar el proceso de KYC.
                  </AlertDescription>
                </Alert>
              )}
            </Step>

            {/* PASO 3: 2FA */}
            <Step
              index={2}
              active={activeStep === 2}
              completed={twoFaStatus?.enabled}
              icon={Shield}
              title="Autenticación de 2 factores (2FA)"
            >
              {twoFaStatus?.enabled ? (
                <Alert variant="success">
                  <CheckCircle2 size={16} />
                  <AlertDescription>
                    2FA activado. Tus operaciones financieras están protegidas.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  <Alert variant="info">
                    <AlertDescription>
                      Para proteger tus payouts y datos bancarios requerimos 2FA. Necesitas
                      una app autenticadora como Google Authenticator, Authy o 1Password.
                    </AlertDescription>
                  </Alert>

                  {!setup2FA && (
                    <Button onClick={start2FASetup}>
                      Iniciar configuración de 2FA
                    </Button>
                  )}

                  {setup2FA && !backupCodes && (
                    <div className="space-y-3">
                      <p className="text-body-sm text-foreground">
                        Escanea este código con tu app autenticadora, o ingresa el secret
                        manualmente:
                      </p>
                      <div className="rounded-md bg-surface-muted p-3 space-y-2 text-caption font-mono break-all">
                        <div>
                          <p className="text-subtle not-italic">Secret:</p>
                          <p className="text-foreground">{setup2FA.secret}</p>
                        </div>
                        <div>
                          <p className="text-subtle not-italic">otpauth URL:</p>
                          <p className="text-foreground">{setup2FA.otpauthUrl}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="space-y-1.5 flex-1">
                          <Label htmlFor="ob-2fa-code">Código de 6 dígitos</Label>
                          <Input
                            id="ob-2fa-code"
                            value={verifyCode}
                            onChange={(e) =>
                              setVerifyCode(
                                e.target.value.replace(/\D/g, '').slice(0, 6)
                              )
                            }
                            inputMode="numeric"
                            pattern="\d{6}"
                            placeholder="123456"
                          />
                        </div>
                        <Button
                          onClick={verify2FASetup}
                          disabled={verifyCode.length !== 6}
                        >
                          Activar 2FA
                        </Button>
                      </div>
                    </div>
                  )}

                  {backupCodes && (
                    <Alert variant="warning">
                      <AlertDescription className="space-y-2">
                        <p>
                          <strong>GUARDA estos códigos de respaldo en un lugar seguro.</strong>
                          Cada uno se puede usar UNA vez si pierdes acceso a tu app
                          autenticadora. No los podrás ver de nuevo.
                        </p>
                        <div className="grid grid-cols-2 gap-2 mt-2 font-mono text-body-sm bg-card p-3 rounded-md">
                          {backupCodes.map((c, i) => (
                            <div key={i}>{c}</div>
                          ))}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </Step>

            {/* PASO 4: Listo */}
            <Step
              index={3}
              active={activeStep >= 3}
              completed={activeStep >= 3}
              icon={CheckCircle2}
              title="¡Listo!"
            >
              <Alert variant="success" className="mb-3">
                <CheckCircle2 size={16} />
                <AlertDescription>
                  Tu cuenta está completamente activada. Ya puedes recibir referidos,
                  generar comisiones y solicitar payouts.
                </AlertDescription>
              </Alert>
              <Button asChild>
                <a href="/dashboard">Ir al dashboard</a>
              </Button>
            </Step>
          </ol>
        </CardContent>
      </Card>

      <Button onClick={fetchAll} variant="ghost" size="sm">
        <RefreshCw size={14} /> Actualizar estado
      </Button>
    </div>
  )
}
